/**
 * Синонимы названия бренда.
 *
 * Экран выглядит служебным, а на деле держит всю математику продукта.
 *
 * Видимость считается по **органическим** ответам: тем, где имени бренда нет в
 * самом вопросе. Если сравнивать только каноническое имя, вопрос с сокращением,
 * латиницей или юрлицом пройдёт как органический — и видимость окажется
 * завышена (спека §8). Поэтому список синонимов — не украшение карточки, а
 * знаменатель всех метрик, и экран говорит об этом словами.
 *
 * Отсюда же поведение после добавления: сервер перепроверяет **всё ядро** и
 * возвращает, сколько вопросов оказались с именем бренда. Это число
 * показывается сразу и с указанием, что делать, — иначе клиент узнает о
 * подпорченном ядре в момент, когда цифра поедет.
 */

import { Link } from 'react-router-dom';

import { useState } from 'react';

import { api } from '../../api/client';
import { ApiError, fieldErrors } from '../../api/problem';
import { useLoad } from '../../ui/load';
import { Confirm, SelectField, TextField } from '../../ui/fields';
import { ErrorNotice, Skeleton } from '../../ui/state';
import { aliasKind, parentAliasNote } from '../../ui/labels';
import { countOf, dateProse } from '@shared/src/format';
import type { components } from '../../api/schema';
import { ORGANIC_RULE } from '@shared/src/product';

type BrandAlias = components['schemas']['BrandAlias'];
type AliasKind = components['schemas']['AliasKind'];
type AliasCreated = components['schemas']['BrandAliasCreated'];

/**
 * Что человек может завести руками.
 *
 * Рода `parent` здесь нет намеренно. Засчитывать упоминание головной сети её
 * точке разрешает отрасль, а не сам синоним: дилерский центр и филиал клиники
 * продают себя именем сети, а мебельная фабрика в торговом центре — нет, и
 * торговый центр ей не сеть. Там, где зачёт не разрешён, синоним заведётся и в
 * замер не пойдёт: контрол, который молча ничего не меняет, обещал бы человеку
 * упоминания, которых он не получит. Головную сеть приносит разбор сайта — и
 * только там, где отрасль это разрешает.
 */
const KINDS: AliasKind[] = [
  'primary',
  'legal',
  'translit',
  'short',
  'misspelling',
  'domain',
];

export function Aliases({ brandId, readOnly }: { brandId: string; readOnly: boolean }) {
  const list = useLoad<BrandAlias[]>(`aliases:${brandId}`, (signal) =>
    api.get<BrandAlias[]>(`/brands/${brandId}/aliases`, { signal }),
  );

  const [text, setText] = useState('');
  const [kind, setKind] = useState<AliasKind>('short');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [recheck, setRecheck] = useState<AliasCreated | null>(null);
  const [removing, setRemoving] = useState<BrandAlias | null>(null);

  const problem = error instanceof ApiError ? error.problem : null;
  const fields = problem ? fieldErrors(problem) : {};

  async function add() {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<AliasCreated>(`/brands/${brandId}/aliases`, {
        alias: text.trim(),
        kind,
      });
      setText('');
      setRecheck(created);
      list.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  async function remove(alias: BrandAlias) {
    setRemoving(null);
    setBusy(true);
    setError(null);
    try {
      await api.delete<void>(`/brands/${brandId}/aliases/${alias.id}`);
      list.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(false);
    }
  }

  if (list.state.kind === 'loading') return <Skeleton rows={2} />;
  if (list.state.kind === 'failed') {
    return (
      <ErrorNotice
        error={list.state.error}
        action={
          <button className="btn btn-ghost btn-sm" type="button" onClick={list.reload}>
            Повторить
          </button>
        }
      />
    );
  }

  const aliases = list.state.data;

  return (
    <div className="two-pane">
      <div className="panel">
        <h2 className="panel-h">Как ещё называют бренд</h2>
        <p className="stamp">
          {ORGANIC_RULE}
          Сравнение по одному каноническому имени пропускает сокращение, латиницу и юрлицо
          — и завышает видимость. Чем полнее список, тем честнее цифра.
        </p>

        {error && !problem?.errors ? <ErrorNotice error={error} /> : null}

        {recheck ? (
          <div
            className={recheck.questions_flagged ? 'notice warn' : 'notice'}
            role="status"
          >
            <b>Синоним добавлен, ядро перепроверено</b>
            {/* «Их надо переписать» без ссылки — задание без адреса: каких
                именно шести, экран не показывал и пойти было некуда. Вопросы с
                именем бренда внутри помечены на экране ядра. */}
            {recheck.questions_flagged ? (
              <>
                {`Перепроверили ${countOf(recheck.questions_rechecked, 'вопрос', 'вопроса', 'вопросов')}, и у ${countOf(recheck.questions_flagged, 'вопроса', 'вопросов', 'вопросов')} внутри нашлось имя бренда. Упоминание в ответе на такой вопрос ничего не доказывает, поэтому их надо переписать — они помечены `}
                <Link to="/questions">в семантическом ядре</Link>.
              </>
            ) : (
              `Перепроверили ${countOf(recheck.questions_rechecked, 'вопрос', 'вопроса', 'вопросов')} — имени бренда ни в одном нет, ядро в порядке.`
            )}
          </div>
        ) : null}

        {aliases.length === 0 ? (
          <p className="panel-lead">
            Синонимов пока нет. Разбор ответов идёт только по каноническому имени, а
            значит упоминание «в сокращении» мы не засчитаем.
          </p>
        ) : (
          <>
            <ul className="finds">
              {aliases.map((alias) => (
                <li key={alias.id}>
                  <b>{alias.alias}</b>
                  <span className="stamp">
                    {aliasKind(alias.kind)}
                    {alias.created_at ? ` · c ${dateProse(alias.created_at)}` : ''}
                  </span>
                  {readOnly ? null : (
                    <button
                      type="button"
                      className="linkish"
                      disabled={busy}
                      onClick={() => setRemoving(alias)}
                    >
                      Убрать
                    </button>
                  )}
                </li>
              ))}
            </ul>
            {/* Род, который человек не заводил и в списке видит впервые:
                объясняем его там, где он стоит, а не в общем описании вкладки. */}
            {aliases.some((alias) => alias.kind === 'parent') ? (
              <p className="stamp">{parentAliasNote()}</p>
            ) : null}
          </>
        )}
      </div>

      {readOnly ? null : (
        <div className="panel">
          <h2 className="panel-h">Добавить вариант написания</h2>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void add();
            }}
          >
            <TextField
              label="Как пишут"
              value={text}
              onChange={setText}
              error={
                fields.alias ?? (problem?.code === 'conflict' ? problem.detail : null)
              }
              placeholder="Автодом Казань"
              hint="Регистр и «ё» не важны — сравнение идёт по нормализованной форме"
            />
            <SelectField
              label="Что это за вариант"
              value={kind}
              onChange={setKind}
              options={KINDS.map((value) => ({ value, label: aliasKind(value) }))}
              hint="Юрлицо и домен — точное написание, остальное свободнее"
            />
            <button
              className="btn btn-primary"
              type="submit"
              disabled={busy || text.trim().length < 2}
            >
              Добавить синоним
            </button>
          </form>
        </div>
      )}

      <Confirm
        open={removing !== null}
        title="Убрать синоним"
        action="Убрать синоним"
        danger
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && void remove(removing)}
      >
        <p>
          {removing ? `«${removing.alias}» ` : ''}перестанет учитываться при разборе
          ответов. Упоминания, найденные по нему раньше, останутся в истории, но новые
          замеры его искать не будут — видимость может просесть, и это будет наша слепота,
          а не падение бренда.
        </p>
      </Confirm>
    </div>
  );
}
